import React, { useCallback, useEffect, useState } from 'react';
import { Eye, EyeOff, Plus, Send, Trash2 } from 'lucide-react';
import {
  PortalMetric,
  PortalTask,
  PortalTaskField,
  portalAdminService,
} from '../../services/api';
import { TaskFieldsEditor, emptyField } from './TaskBuilder';
import { PortalMetricsPanel } from './PortalMetricsPanel';

const fa = (n: number) => n.toLocaleString('fa-IR');
/** نیولاین به‌صورت ثابت، برای متن چندخطی confirm */
const NEWLINE = String.fromCharCode(10);
const inputClass =
  'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none transition focus:border-blue-500';

interface Props {
  programId: string;
  sessions: any[];
  tracks: any[];
  cohorts: any[];
  onError: (err: any, fallback: string) => void;
  onFlash: (message: string) => void;
}

const emptyDraft = () => ({
  title: '',
  description: '',
  sessionId: '',
  trackId: '',
  audience: 'PROGRAM' as 'PROGRAM' | 'COHORT' | 'INDIVIDUAL',
  dueAt: '',
  requiresReview: true,
  allowResubmit: true,
  allowEditAfterSubmit: false,
  maxScore: '',
});

/** ساخت و مدیریت تسک‌ها — این صفحه‌ای است که پشتیبانی با آن کار می‌کند */
export const PortalTasksTab: React.FC<Props> = ({
  programId,
  sessions,
  tracks,
  cohorts,
  onError,
  onFlash,
}) => {
  const [tasks, setTasks] = useState<PortalTask[]>([]);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState(emptyDraft());
  const [fields, setFields] = useState<PortalTaskField[]>([emptyField()]);
  const [selectedCohorts, setSelectedCohorts] = useState<string[]>([]);
  const [metrics, setMetrics] = useState<PortalMetric[]>([]);

  const load = useCallback(async () => {
    try {
      setTasks(await portalAdminService.listTasks(programId));
    } catch (err: any) {
      onError(err, 'دریافت تسک‌ها انجام نشد.');
    }
  }, [programId, onError]);

  /**
   * شاخص‌ها جدا از تسک‌ها بارگذاری می‌شوند چون سطح دوره‌اند و با هر
   * ذخیرهٔ تسک عوض نمی‌شوند؛ فقط وقتی شاخص تازه ساخته شود به‌روز می‌گردند.
   */
  const loadMetrics = useCallback(async () => {
    try {
      setMetrics(await portalAdminService.listMetrics(programId));
    } catch (err: any) {
      onError(err, 'دریافت شاخص‌ها انجام نشد.');
    }
  }, [programId, onError]);

  useEffect(() => {
    load();
    loadMetrics();
  }, [load, loadMetrics]);

  const createMetric = async (title: string): Promise<PortalMetric | null> => {
    try {
      const created = await portalAdminService.createMetric({ title }, programId);
      setMetrics((prev) => [...prev, created]);
      onFlash('شاخص «' + created.title + '» ساخته شد.');
      return created;
    } catch (err: any) {
      onError(err, 'ساخت شاخص انجام نشد.');
      return null;
    }
  };

  const run = async (fn: () => Promise<void>, fallback: string) => {
    setBusy(true);
    try {
      await fn();
    } catch (err: any) {
      onError(err, fallback);
    } finally {
      setBusy(false);
    }
  };

  /**
   * حذف تسک — حتی وقتی پاسخ ثبت‌شده دارد.
   *
   * تعداد پاسخ‌ها را از قبل داریم، پس یک‌بار و صریح می‌پرسیم و همان‌جا با force
   * می‌فرستیم. مدل قبلی اول درخواست بی‌force می‌فرستاد و منتظر رد شدن سرور
   * می‌ماند؛ آن رد شدن یک پیام قرمز بالای صفحه می‌انداخت که مثل شکست حذف
   * به نظر می‌رسید، بعد تازه سؤال دوم می‌آمد.
   */
  const removeTask = (task: PortalTask) =>
    run(async () => {
      const answered = task.submittedCount ?? 0;
      const assigned = task._count?.assignments ?? 0;

      const warning = answered
        ? 'روی این تسک ' +
          fa(answered) +
          ' پاسخ ثبت شده است. با حذف تسک، پاسخ‌ها، بازخوردها و فایل‌های آپلودشده هم پاک می‌شوند و برگشتی ندارد.' +
          NEWLINE +
          NEWLINE +
          'اگر فقط می‌خواهید از دسترس شرکت‌کننده‌ها خارج شود، به‌جای حذف آن را از حالت انتشار خارج کنید.'
        : assigned
          ? 'این تسک به ' + fa(assigned) + ' نفر تخصیص یافته ولی هنوز کسی پاسخی نداده است.'
          : '';

      const confirmed = window.confirm(
        'تسک «' + task.title + '» حذف شود؟' + (warning ? NEWLINE + NEWLINE + warning : ''),
      );
      if (!confirmed) return;

      await portalAdminService.deleteTask(task.id, answered > 0);
      await load();
      onFlash(answered ? 'تسک و ' + fa(answered) + ' پاسخ آن حذف شد.' : 'تسک حذف شد.');
    }, 'حذف تسک انجام نشد.');

  const startNew = () => {
    setEditing('new');
    setDraft(emptyDraft());
    setFields([emptyField()]);
    setSelectedCohorts([]);
  };

  const startEdit = async (taskId: string) => {
    const task = await portalAdminService.getTask(taskId);
    setEditing(taskId);
    setDraft({
      title: task.title,
      description: task.description ?? '',
      sessionId: task.sessionId ?? '',
      trackId: task.trackId ?? '',
      audience: task.audience as any,
      dueAt: task.dueAt ? task.dueAt.slice(0, 10) : '',
      requiresReview: task.requiresReview,
      allowResubmit: task.allowResubmit,
      allowEditAfterSubmit: task.allowEditAfterSubmit,
      maxScore: task.maxScore ? String(task.maxScore) : '',
    });
    setFields(task.fields.length ? task.fields : [emptyField()]);
  };

  const payload = () => ({
    ...draft,
    dueAt: draft.dueAt || undefined,
    sessionId: draft.sessionId || undefined,
    trackId: draft.trackId || undefined,
    maxScore: draft.maxScore ? Number(draft.maxScore) : undefined,
    fields: fields.map((f, i) => ({ ...f, sortOrder: i })),
  });

  const save = () =>
    run(async () => {
      if (!draft.title.trim()) {
        onError(null, 'عنوان تسک الزامی است.');
        return;
      }
      if (fields.some((f) => !f.label.trim())) {
        onError(null, 'همهٔ فیلدها باید عنوان داشته باشند.');
        return;
      }
      if (editing === 'new') {
        const created = await portalAdminService.createTask(programId, payload());
        setEditing(created.id);
        onFlash('تسک ساخته شد. حالا می‌توانید منتشرش کنید.');
      } else if (editing) {
        await portalAdminService.updateTask(editing, payload());
        onFlash('تسک ذخیره شد.');
      }
      await load();
    }, 'ذخیرهٔ تسک انجام نشد.');

  const publish = (taskId: string) =>
    run(async () => {
      const result = await portalAdminService.publishTask(taskId, {
        cohortIds: draft.audience === 'COHORT' ? selectedCohorts : undefined,
        dueAt: draft.dueAt || undefined,
      });
      await load();
      onFlash(fa(result.assigned) + ' نفر تخصیص گرفتند.');
    }, 'انتشار تسک انجام نشد.');

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm font-bold text-gray-600">{fa(tasks.length)} تسک</p>
        <button
          type="button"
          onClick={startNew}
          className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-2 text-sm font-bold text-white"
        >
          <Plus className="h-4 w-4" />
          تسک جدید
        </button>
      </div>

      <PortalMetricsPanel
        metrics={metrics}
        onChanged={loadMetrics}
        onError={onError}
        onFlash={onFlash}
      />

      {editing ? (
        <div className="rounded-xl border border-blue-200 bg-blue-50/30 p-4">
          <p className="mb-3 text-sm font-bold text-gray-900">
            {editing === 'new' ? 'تسک جدید' : 'ویرایش تسک'}
          </p>

          <div className="grid gap-3 md:grid-cols-2">
            <label className="block md:col-span-2">
              <span className="mb-1 block text-xs font-bold text-gray-600">عنوان تسک</span>
              <input
                value={draft.title}
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                className={inputClass}
              />
            </label>
            <label className="block md:col-span-2">
              <span className="mb-1 block text-xs font-bold text-gray-600">
                توضیح و دستورالعمل
              </span>
              <textarea
                rows={3}
                value={draft.description}
                onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                className={inputClass}
              />
            </label>

            <label className="block">
              <span className="mb-1 block text-xs font-bold text-gray-600">جلسه</span>
              <select
                value={draft.sessionId}
                onChange={(e) => setDraft({ ...draft, sessionId: e.target.value })}
                className={inputClass}
              >
                <option value="">— بدون جلسه —</option>
                {sessions.map((s) => (
                  <option key={s.id} value={s.id}>
                    {fa(s.number)} · {s.title}
                  </option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="mb-1 block text-xs font-bold text-gray-600">محور ۶C</span>
              <select
                value={draft.trackId}
                onChange={(e) => setDraft({ ...draft, trackId: e.target.value })}
                className={inputClass}
              >
                <option value="">— بدون محور —</option>
                {tracks.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.title}
                  </option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="mb-1 block text-xs font-bold text-gray-600">مخاطب</span>
              <select
                value={draft.audience}
                onChange={(e) => setDraft({ ...draft, audience: e.target.value as any })}
                className={inputClass}
              >
                <option value="PROGRAM">همهٔ شرکت‌کنندگان</option>
                <option value="COHORT">گروه‌های انتخابی</option>
                <option value="INDIVIDUAL">افراد مشخص</option>
              </select>
            </label>

            <label className="block">
              <span className="mb-1 block text-xs font-bold text-gray-600">مهلت تحویل</span>
              <input
                type="date"
                value={draft.dueAt}
                onChange={(e) => setDraft({ ...draft, dueAt: e.target.value })}
                className={inputClass}
              />
            </label>
          </div>

          {draft.audience === 'COHORT' ? (
            <div className="mt-3 rounded-lg border border-gray-200 bg-white p-3">
              <p className="mb-2 text-xs font-bold text-gray-600">گروه‌های مقصد</p>
              <div className="flex flex-wrap gap-2">
                {cohorts.map((c) => {
                  const active = selectedCohorts.includes(c.id);
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() =>
                        setSelectedCohorts(
                          active
                            ? selectedCohorts.filter((x) => x !== c.id)
                            : [...selectedCohorts, c.id],
                        )
                      }
                      className={
                        'rounded-lg px-3 py-1.5 text-xs font-bold transition ' +
                        (active ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600')
                      }
                    >
                      {c.name}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}

          <div className="mt-3 flex flex-wrap gap-4 rounded-lg border border-gray-200 bg-white p-3">
            <label className="flex items-center gap-2 text-xs font-bold text-gray-700">
              <input
                type="checkbox"
                checked={draft.requiresReview}
                onChange={(e) => setDraft({ ...draft, requiresReview: e.target.checked })}
              />
              نیاز به تأیید منتور
            </label>
            <label className="flex items-center gap-2 text-xs font-bold text-gray-700">
              <input
                type="checkbox"
                checked={draft.allowResubmit}
                onChange={(e) => setDraft({ ...draft, allowResubmit: e.target.checked })}
              />
              امکان ارسال مجدد پس از اصلاح
            </label>
            <label className="flex items-center gap-2 text-xs font-bold text-gray-700">
              <input
                type="checkbox"
                checked={draft.allowEditAfterSubmit}
                onChange={(e) => setDraft({ ...draft, allowEditAfterSubmit: e.target.checked })}
              />
              ویرایش پس از ارسال
            </label>
            <label className="flex items-center gap-2 text-xs font-bold text-gray-700">
              امتیاز کامل:
              <input
                type="number"
                value={draft.maxScore}
                onChange={(e) => setDraft({ ...draft, maxScore: e.target.value })}
                className="w-20 rounded-lg border border-gray-300 px-2 py-1 text-xs"
              />
            </label>
          </div>

          <div className="mt-4">
            <p className="mb-2 text-sm font-bold text-gray-800">فرمت پاسخ</p>
            <TaskFieldsEditor
              fields={fields}
              onChange={setFields}
              metrics={metrics}
              onCreateMetric={createMetric}
            />
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={save}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-bold text-white disabled:opacity-50"
            >
              ذخیرهٔ تسک
            </button>
            {editing !== 'new' ? (
              <button
                type="button"
                disabled={busy}
                onClick={() => publish(editing)}
                className="flex items-center gap-1.5 rounded-lg bg-green-600 px-4 py-2 text-sm font-bold text-white disabled:opacity-50"
              >
                <Send className="h-4 w-4" />
                انتشار و تخصیص
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => setEditing(null)}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-bold text-gray-600"
            >
              بستن
            </button>
          </div>
        </div>
      ) : null}

      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
        <table className="w-full min-w-[780px] text-right text-sm">
          <thead className="bg-gray-50 text-[11px] font-bold text-gray-500">
            <tr>
              <th className="px-4 py-3">عنوان</th>
              <th className="px-4 py-3">جلسه</th>
              <th className="px-4 py-3">محور</th>
              <th className="px-4 py-3">فیلد</th>
              <th className="px-4 py-3">تخصیص</th>
              <th className="px-4 py-3">وضعیت</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {tasks.map((task) => (
              <tr key={task.id} className="hover:bg-blue-50/30">
                <td className="px-4 py-3">
                  <button
                    type="button"
                    onClick={() => startEdit(task.id)}
                    className="text-right font-bold text-gray-900 hover:text-blue-700"
                  >
                    {task.title}
                  </button>
                </td>
                <td className="px-4 py-3 text-xs text-gray-500">
                  {task.session ? fa(task.session.number) + ' · ' + task.session.title : '—'}
                </td>
                <td className="px-4 py-3 text-xs text-gray-500">{task.track?.title ?? '—'}</td>
                <td className="px-4 py-3 text-xs font-bold tabular-nums text-gray-600">
                  {fa(task.fields?.length ?? 0)}
                </td>
                <td className="px-4 py-3 text-xs font-bold tabular-nums text-gray-600">
                  {fa(task._count?.assignments ?? 0)}
                  {task.submittedCount ? (
                    /* تعداد پاسخ‌ها همان چیزی است که حذف نابودش می‌کند؛ باید در ردیف دیده شود */
                    <span className="mr-1 text-[10.5px] font-bold text-violet-700">
                      ({fa(task.submittedCount)} پاسخ)
                    </span>
                  ) : null}
                </td>
                <td className="px-4 py-3">
                  <span
                    className={
                      'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-bold ' +
                      (task.isPublished ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500')
                    }
                  >
                    {task.isPublished ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                    {task.isPublished ? 'منتشر' : 'پیش‌نویس'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <button
                    type="button"
                    onClick={() => removeTask(task)}
                    disabled={busy}
                    className="rounded p-1 text-red-500 transition hover:bg-red-50 disabled:opacity-40"
                    aria-label={task.submittedCount ? 'حذف تسک و پاسخ‌هایش' : 'حذف تسک'}
                    title={task.submittedCount ? 'حذف تسک و پاسخ‌هایش' : 'حذف تسک'}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </td>
              </tr>
            ))}
            {!tasks.length ? (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center font-bold text-gray-400">
                  هنوز تسکی ساخته نشده است.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
};
